import React, { useEffect } from 'react';
import { MDText } from 'i18n-react';

import { Button, UploadIcon } from '@motorway/mw-highway-code';
import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { triggerBulkBiddingProcessingFailed } from 'Services/analytics/events/bulkBiddingEvents/triggerBulkBiddingProcessingFailed';
import { triggerBulkBiddingUploadButtonClicked } from 'Services/analytics/events/bulkBiddingEvents/triggerBulkBiddingUploadButtonClicked';
import { triggerBulkBiddingUploadButtonHovered } from 'Services/analytics/events/bulkBiddingEvents/triggerBulkBiddingUploadButtonHovered';
import { logger } from 'Services/logger/logger';
import { Tooltip } from 'Storybook/core/Tooltip';

import { BulkBiddingModal } from './BulkBiddingModal/BulkBiddingModal';
import bulkBiddingTexts from './BulkBidding.texts';
import { useBulkBiddingModal } from './BulkBiddingModalProvider';
import { useBulkBiddingUpload } from './useBulkBiddingUpload';
import { useFetchVehiclesForBulkBidding } from './useFetchVehiclesForBulkBiddingCSV';

import styles from './BulkBidding.module.scss';

const LocalT = new MDText(bulkBiddingTexts);
export const BulkBidding: React.FC = () => {
	const { handleCloseModal, handleOpenModal, isModalOpen } = useBulkBiddingModal();

	const { handleFileChange, inputRef, resetUpload, triggerFileInput, validatedCSVRows } =
		useBulkBiddingUpload(handleOpenModal);

	const { data: vehicles, error, isError, isLoading } = useFetchVehiclesForBulkBidding(validatedCSVRows);

	const hasVehicles = !!(vehicles?.actionable.length || vehicles?.unactionable.length);

	useEffect(() => {
		if (isError) {
			triggerBulkBiddingProcessingFailed('Vehicle list fetch failed');
			toast({
				content: LocalT.translate('toast.failedToFetchVehicles.content'),
				heading: LocalT.translate('toast.failedToFetchVehicles.heading'),
				variant: 'error',
			});
			logger.error({
				error: error instanceof Error ? error : new Error('Failed to fetch vehicles for bulk bidding'),
				message: 'Failed to fetch vehicles for bulk bidding',
				scope: 'BulkBiddingModal',
			});
			return;
		}

		if (!isLoading && !vehicles.actionable.length && !vehicles.unactionable.length) {
			triggerBulkBiddingProcessingFailed('No live sale vehicles');
			toast({
				content: LocalT.translate('toast.noLiveSaleVehicles.content'),
				heading: LocalT.translate('toast.noLiveSaleVehicles.heading'),
				variant: 'error',
			});
		}
	}, [isError, error, isLoading, vehicles?.actionable.length, vehicles?.unactionable.length]);

	const handleDismissModal = () => {
		resetUpload();
		handleCloseModal();
	};

	const shouldShowModal = isModalOpen && !isLoading && !isError && hasVehicles;

	return (
		<div onFocusCapture={(event) => event.stopPropagation()}>
			{shouldShowModal ? <BulkBiddingModal handleDismissModal={handleDismissModal} vehicles={vehicles} /> : null}
			<Tooltip content={<div className={styles.tooltip}>{LocalT.translate('tooltip')}</div>}>
				<div className={styles.uploadButtonContainer}>
					<Button
						icon={UploadIcon}
						label={LocalT.translate('tooltip')}
						onClick={() => {
							triggerBulkBiddingUploadButtonClicked();
							triggerFileInput();
						}}
						onMouseEnter={() => triggerBulkBiddingUploadButtonHovered()}
						showLabel={false}
						size="medium"
						type="button"
						variant="secondary"
					/>
				</div>
			</Tooltip>
			<input
				ref={inputRef}
				accept=".csv"
				className={styles.input}
				data-testid="uploadCSV"
				onChange={handleFileChange}
				type="file"
			/>
		</div>
	);
};
